import React, { useEffect } from "react";
import { useForm } from "react-hook-form";
import { Typography, Box, Button, Divider, Grid, Alert } from "@mui/material";
import ErrorAlert from "../../../components/alerts/ErrorAlert";
import {
  useGetTenantInformationQuery,
  useUpdateTenantBillingMutation,
} from "../../../features/tenants/tenantsApiSlice";
import Loading from "../../../components/spinners/Loading";
import TextField from "../../../components/form/fields/TextField";
import IBANTextField from "../../../components/form/fields/IBANTextField";

const BillingSettings = () => {
  const {
    data: tenant,
    refetch,
    isLoading,
    isFetching,
  } = useGetTenantInformationQuery();
  const [error, setError] = React.useState(null);
  const [success, setSuccess] = React.useState(false);
  const [updateTenantBilling, { isLoading: isUpdating }] = useUpdateTenantBillingMutation();

  const { handleSubmit, control, reset, setError: setFieldError, clearErrors } = useForm({
    defaultValues: {
      billingAccountHolder: "",
      billingIban: "",
      firstName: "",
      lastName: "",
    },
  });

  useEffect(() => {
    if (tenant) {
      const [firstName = "", ...lastNameParts] = (tenant.billingContactName || "").split(" ");
      const lastName = lastNameParts.join(" ");

      reset({
        billingAccountHolder: tenant.billingAccountHolder || "",
        billingIban: tenant.billingIban || "",
        firstName,
        lastName,
      });
    }
  }, [tenant, reset]);

  const onSubmit = async (data) => {
    setError(null);
    setSuccess(false);

    try {
      await updateTenantBilling(data).unwrap();
      setSuccess(true);
      refetch();
    } catch (err) {
      console.error("Error updating billing:", err);
      setError(err?.data?.message || "Fehler beim Speichern der Abrechnungsdaten");
    }
  };

  if (isLoading || isFetching) {
    return <Loading />;
  }

  return (
    <>
      <Typography variant="h3" sx={{ mb: 2 }}>
        Abrechnung
      </Typography>
      <Divider sx={{ mb: 3 }} />
      <Typography sx={{ my: 2 }} variant="body1">
        Hier können Sie Ihre Abrechnungsdaten für die monatliche Abbuchung per SEPA-Lastschrift anpassen.
      </Typography>
      <Alert severity="info" sx={{ mb: 3 }}>
        Die Abbuchung erfolgt monatlich per SEPA-Lastschrift. Ihr Vertrag hat eine Mindestlaufzeit von 1 Jahr und verlängert sich automatisch um jeweils 1 Jahr, wenn nicht gekündigt wird.
      </Alert>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              name="firstName"
              label="Vorname (Rechnungskontakt)"
              control={control}
              required="Bitte angeben"
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              name="lastName"
              label="Nachname (Rechnungskontakt)"
              control={control}
              required="Bitte angeben"
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              name="billingAccountHolder"
              label="Kontoinhaber"
              control={control}
              required="Bitte angeben"
            />
          </Grid>
          <Grid item xs={12}>
            <IBANTextField
              fullWidth
              name="billingIban"
              label="IBAN"
              required="Bitte eine IBAN eingeben"
              control={control}
              setError={setFieldError}
              clearErrors={clearErrors}
            />
          </Grid>
        </Grid>

        {success && (
          <Alert severity="success" sx={{ mt: 2 }}>
            Abrechnungsdaten erfolgreich gespeichert.
          </Alert>
        )}
        {error && <ErrorAlert errorMessage={error} />}

        <Box display="flex" justifyContent="flex-end">
          <Button
            variant="contained"
            color="secondary"
            type="submit"
            fullWidth
            disabled={isUpdating}
            sx={{ mt: 2 }}
          >
            {isUpdating ? "Speichert..." : "SPEICHERN"}
          </Button>
        </Box>
      </form>
    </>
  );
};

export default BillingSettings;
